import React, { useState, useEffect } from 'react';
import { auth, provider, db } from '../firebaseConfig';
import { signInWithEmailAndPassword, signInWithPopup, signOut, OAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import { FaGoogle } from 'react-icons/fa';
import { getMessaging, getToken } from 'firebase/messaging';
import '../styles/Login.css';
import '../styles/LandingPage.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      updateLoginDate(user);
      checkUserProfile(user);
      checkAndSaveFcmToken(user.uid); // ← ログイン時に実行
    }
  }, [user]);

  const checkAndSaveFcmToken = async (uid) => {
    try {
      const tokenRef = doc(db, 'tokens', uid);
      const tokenSnap = await getDoc(tokenRef);
      let approve = true;

      if (tokenSnap.exists()) {
        const data = tokenSnap.data();
        approve = data.approve !== false; // 明示的にfalseなら無効
      } else {
        // approveフィールドがないなら初期値trueで保存
        await setDoc(tokenRef, { approve: true }, { merge: true });
      }

      if (!approve) {
        console.log('🔕 ユーザーは通知を許可していないためトークン保存スキップ');
        await setDoc(tokenRef, { token: null }, { merge: true });
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('🔕 通知の許可がされませんでした');
        return;
      }

      const messaging = getMessaging();
      const currentToken = await getToken(messaging, {
        vapidKey: 'BOQlFlynawoiv-mauechKpC6F0LKgUudbuzcfO80eqJhlIdDUqPKR-VVnw-cJ6j2lG3_lRqtuWztwIK0SflphQo',
      });

      if (currentToken) {
        await setDoc(tokenRef, { token: currentToken }, { merge: true });
        console.log('✅ FCMトークン取得＆保存成功:', currentToken);
      } else {
        console.warn('⚠️ トークンが取得できませんでした');
      }
    } catch (error) {
      console.error('🚨 トークン保存エラー:', error);
    }
  };

  const checkUserProfile = async (user) => {
    const docRef = doc(db, 'members', user.uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const profile = docSnap.data();
      if (!profile.onboardingCompleted) {
        navigate('/complete-profile0');
      } else {
        navigate('/member_search');
      }
    } else {
      await setDoc(docRef, {
        onboardingCompleted: false,
        login_date: new Date()
      });
      navigate('/onboarding');
    }
  };

  const updateLoginDate = async (user) => {
    const docRef = doc(db, 'members', user.uid);
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        await updateDoc(docRef, { login_date: new Date() });
      } else {
        await setDoc(docRef, { login_date: new Date(), onboardingCompleted: false }, { merge: true });
      }
    } catch (error) {
      console.error('Error updating login date:', error);
    }
  };

  const handleLogin = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (user.emailVerified) {
        setUser(user);
      } else {
        setError('メールアドレスの確認が完了していません。メールを確認してください。');
        await signOut(auth);
      }
    } catch (error) {
      console.error("Error logging in with email and password", error);
      setError('ログインに失敗しました。メールアドレスまたはパスワードが正しくありません。');
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const docRef = doc(db, 'members', user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setUser(user);
      } else {
        setError('アカウントが登録されていません。会員登録を行ってください。');
        await signOut(auth);
      }
    } catch (error) {
      console.error("Error logging in with Google", error);
      setError('Googleでのログインに失敗しました。');
    }
  };

  const handleAppleLogin = async () => {
    const appleProvider = new OAuthProvider('apple.com');

    try {
      const result = await signInWithPopup(auth, appleProvider);
      const user = result.user;

      console.log("✅ Apple ログイン成功:", user);

      const docRef = doc(db, 'members', user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setUser(user);
      } else {
        await setDoc(docRef, {
          email: user.email || null,
          name: user.displayName || "",
          onboardingCompleted: false,
          login_date: new Date()
        });
        setUser(user);
        navigate('/onboarding');
      }
    } catch (error) {
      console.error("🚨 Apple ログインエラー:", error);
      setError('Appleでのログインに失敗しました。');
    }
  };

  return (
    <div className="login-container">
      <header className="header-top">
        <div className="logo">
          <Link to="/"><img src="/images/default1.png" alt="Logo" /></Link>
          Ace Match
        </div>
      </header>
      <h2 className="login-title">ログイン</h2>
      {error && <p className="login-error">{error}</p>}
      <input type="email" placeholder="メールアドレス" onChange={(e) => setEmail(e.target.value)} className="login-input" />
      <input type="password" placeholder="パスワード" onChange={(e) => setPassword(e.target.value)} className="login-input" />

      <button onClick={handleLogin} className="signup-button">
        メールアドレスでログイン
      </button>

      <button onClick={handleGoogleLogin} className="google-signup-button">
        <img
          src="https://developers.google.com/identity/images/g-logo.png"
          alt="Google Logo"
          className="google-logo"
        />
        Googleでログイン
      </button>

      <button onClick={handleAppleLogin} className="google-signup-button">
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg"
          alt="Apple Logo"
          className="social-logo"
        />
        Appleでログイン
      </button>

      <p className="login-signup-link"><Link to="/signup">会員登録はこちら</Link></p>
      <p className="login-signup-link"><Link to="/reset-password">パスワード忘れた方</Link></p>
    </div>
  );
};

export default Login;
